import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Job, JobListResponse, MetricsResponse, ErrorResponse, FilterState, S3StatusResponse, S3RequeueResponse } from '../types';

/**
 * API Service Module
 * 
 * Handles all HTTP communication with the backend API Gateway.
 * Includes authentication token management and error handling.
 * 
 * Requirements: 1.1, 3.1, 5.1
 */

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Create Axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * Adds Authorization header with access token to all requests
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get access token from auth service (will be implemented in task 11.3)
    const token = getAccessToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('[api] No token available for request');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles various error responses and transforms them into user-friendly messages
 */
apiClient.interceptors.response.use(
  (response) => {
    // Return successful response as-is
    return response;
  },
  (error: AxiosError<ErrorResponse>) => {
    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const errorData = error.response.data;
      
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          handleUnauthorized();
          break;
        case 403:
          // Forbidden - access denied
          throw new Error('Access denied. You do not have permission to access this resource.');
        case 404:
          // Not found
          throw new Error(errorData?.message || 'The requested resource was not found.');
        case 500:
          // Internal server error
          throw new Error('Service unavailable. Please try again later.');
        case 503:
          // Service unavailable
          throw new Error('Unable to connect to the dashboard service. Please try again later.');
        case 504:
          // Gateway timeout
          throw new Error('Request timeout. The server took too long to respond.');
        default:
          // Other errors
          throw new Error(errorData?.message || 'An unexpected error occurred.');
      }
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Connection failed. Please check your network connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred.');
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get access token from auth service
 */
function getAccessToken(): string | null {
  // Import auth service dynamically to avoid circular dependencies
  const { getToken } = require('./auth');
  const token = getToken();
  return token;
}

/**
 * Handle unauthorized access
 * Redirects user to login page
 */
function handleUnauthorized(): void {
  // Import auth service dynamically to avoid circular dependencies
  const { login } = require('./auth');
  console.error('Unauthorized access - token expired or invalid');
  // Redirect to Cognito hosted UI
  login();
}

/**
 * API Methods
 */

/**
 * List jobs with optional filters and pagination
 * 
 * @param filters - Optional filter criteria (site, press, status, date range)
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 50)
 * @returns Promise with job list response
 */
export async function listJobs(
  filters?: FilterState,
  page: number = 1,
  limit: number = 50
): Promise<JobListResponse> {
  const params: Record<string, string | number> = {
    page,
    limit,
  };
  
  // Add filters to query parameters if provided
  if (filters?.press) {
    params.press = filters.press;
  }
  if (filters?.site) {
    params.site = filters.site;
  }
  if (filters?.status) {
    params.status = filters.status;
  }
  if (filters?.startDate) {
    params.startDate = filters.startDate;
  }
  if (filters?.endDate) {
    params.endDate = filters.endDate;
  }
  if (filters?.excludePress) {
    params.excludePress = filters.excludePress;
  }
  
  const response = await apiClient.get<JobListResponse>('/jobs', { params });
  return response.data;
}

/**
 * Get a single job by ID
 * 
 * @param jobId - Job identifier
 * @returns Promise with job data
 */
export async function getJob(jobId: string): Promise<Job> {
  const response = await apiClient.get<Job>(`/jobs/${jobId}`);
  return response.data;
}

/**
 * Search jobs by filename or job ID
 * 
 * @param query - Search query string
 * @returns Promise with matching jobs
 */
export async function searchJobs(query: string): Promise<{ jobs: Job[]; totalCount: number }> {
  const response = await apiClient.get<{ jobs: Job[]; totalCount: number }>('/jobs/search', {
    params: { q: query },
  });
  return response.data;
}

/**
 * Get system health metrics
 * 
 * @param period - Time period for metrics (24h, 7d, 30d)
 * @returns Promise with metrics data
 */
export async function getMetrics(period: '1h' | '24h' | '7d' | '30d' = '24h'): Promise<MetricsResponse> {
  const response = await apiClient.get<MetricsResponse>('/metrics', {
    params: { period },
  });
  return response.data;
}

/**
 * Reprocess a single paused job, optionally routing to a specific destination press
 * 
 * @param jobId - Job identifier
 * @param destinationPress - Optional destination press to route the job to
 * @returns Promise with reprocess response
 */
export async function reprocessJob(
  jobId: string,
  destinationPress?: string
): Promise<{ message: string; jobId: string; destinationPress: string }> {
  const body: Record<string, string> = {};
  if (destinationPress) {
    body.destinationPress = destinationPress;
  }
  const response = await apiClient.post<{ message: string; jobId: string; destinationPress: string }>(
    `/jobs/${jobId}/reprocess`,
    body
  );
  return response.data;
}

/**
 * Check if a failed job is eligible for retry
 * Verifies the source file still exists in the Source S3 Bucket
 *
 * @param jobId - Job identifier
 * @returns Promise with eligibility status and optional reason
 */
export async function checkRetryEligibility(
  jobId: string
): Promise<{ eligible: boolean; reason?: string }> {
  const response = await apiClient.get<{ eligible: boolean; reason?: string }>(
    `/jobs/${jobId}/retry-eligible`
  );
  return response.data;
}

/**
 * Retry a failed job, optionally routing to a different destination press
 *
 * @param jobId - Job identifier
 * @param destinationPress - Optional destination press to route the job to
 * @returns Promise with retry response
 */
export async function retryJob(
  jobId: string,
  destinationPress?: string
): Promise<{ message: string; jobId: string; destinationPress: string }> {
  const body: Record<string, string> = {};
  if (destinationPress) {
    body.destinationPress = destinationPress;
  }
  const response = await apiClient.post<{ message: string; jobId: string; destinationPress: string }>(
    `/jobs/${jobId}/retry`,
    body
  );
  return response.data;
}

/**
 * Get S3 source bucket file counts per press folder.
 */
export async function getS3Status(): Promise<S3StatusResponse> {
  const response = await apiClient.get<S3StatusResponse>('/monitoring/s3-status');
  return response.data;
}

/**
 * Requeue S3 files to the job queue for reprocessing.
 * Pass press folder name (e.g. "MKE_HP_01") to requeue one press, or omit for all.
 */
export async function requeueS3Files(press?: string): Promise<S3RequeueResponse> {
  const body = press ? { press } : {};
  const response = await apiClient.post<S3RequeueResponse>('/monitoring/s3-requeue', body);
  return response.data;
}

/**
 * Export the configured API client for advanced usage
 */
export default apiClient;
